import { type ChangeEvent, type ReactNode, useState } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import { useUiText } from './text.tsx';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

export function Button({
  variant = 'primary',
  size = 'm',
  block,
  loading,
  disabled,
  icon,
  onClick,
  type = 'button',
  children,
}: {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive';
  size?: 'm' | 'l';
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  icon?: IconName;
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      className={cx('ul-btn', `ul-btn--${variant}`, `ul-btn--${size}`, block && 'ul-btn--block')}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
    >
      {loading ? <span className="ul-spinner" aria-hidden /> : icon ? <Icon name={icon} size={18} /> : null}
      <span>{children}</span>
    </button>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'crit' | 'accent';
  children: ReactNode;
}) {
  return <span className={cx('ul-badge', `ul-badge--${tone}`)}>{children}</span>;
}

export type ServerState = 'ok' | 'busy' | 'provisioning' | 'off' | 'error';
const LEVEL: Record<ServerState, number> = { ok: 4, busy: 2, error: 1, off: 0, provisioning: 4 };

export function SignalMeter({
  state = 'ok',
  showLabel,
  label,
}: {
  state?: ServerState;
  showLabel?: boolean;
  label?: string;
}) {
  const ui = useUiText();
  const text = label ?? ui.serverState[state];
  const meter = (
    <span className={cx('ul-sig', `ul-sig--${state}`)} role="img" aria-label={text} title={text}>
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={cx('ul-sig__bar', i < LEVEL[state] && 'is-on')}
          style={{ height: 4 + i * 4, animationDelay: `${i * 120}ms` }}
        />
      ))}
    </span>
  );
  return showLabel ? (
    <span className="ul-sig-wrap">
      {meter}
      <span>{text}</span>
    </span>
  ) : (
    meter
  );
}

export function ListGroup({
  header,
  footer,
  children,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="ul-group-wrap">
      {header ? <div className="ul-group__header">{header}</div> : null}
      <div className="ul-group">{children}</div>
      {footer ? <div className="ul-group__footer">{footer}</div> : null}
    </section>
  );
}

export function ListRow({
  title,
  subtitle,
  icon,
  leading,
  tone,
  value,
  monoValue,
  mono,
  chevron,
  trailing,
  onClick,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: IconName;
  /** Anything in the icon slot (a flag, an avatar); `icon` wins when both are given. */
  leading?: ReactNode;
  tone?: 'warn' | 'crit';
  value?: ReactNode;
  monoValue?: boolean;
  mono?: boolean;
  chevron?: boolean;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      {icon ? (
        <span className={cx('ul-row__icon', tone && `ul-row__icon--${tone}`)}>
          <Icon name={icon} size={18} />
        </span>
      ) : leading ? (
        <span className="ul-row__leading">{leading}</span>
      ) : null}
      <span className="ul-row__body">
        <span className={cx('ul-row__title', mono && 'ul-mono')}>{title}</span>
        {subtitle ? <span className="ul-row__subtitle">{subtitle}</span> : null}
      </span>
      {value != null ? <span className={cx('ul-row__value', monoValue && 'ul-mono')}>{value}</span> : null}
      {trailing}
      {chevron ? <Icon name="chevron" size={18} className="ul-row__chev" /> : null}
    </>
  );
  const className = cx('ul-row', !!(icon || leading) && 'ul-row--icon');
  return onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  );
}

export function Chip({
  selected,
  mono,
  onClick,
  children,
}: {
  selected?: boolean;
  mono?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="ul-chip" aria-pressed={!!selected} onClick={onClick}>
      {mono ? <span className="ul-mono">{children}</span> : children}
    </button>
  );
}

/** Round country flag (SVG). Decorative: put the country or city name in text next to it. */
export function Flag({ country, size = 24 }: { country: string; size?: number }) {
  if (!/^[A-Za-z]{2}$/.test(country)) return null;
  return (
    <span
      className={`fi fis fi-${country.toLowerCase()} ul-flag`}
      style={{ width: size, height: size }}
      aria-hidden
    />
  );
}

/**
 * One choice in a single-select list (radio pattern). Wrap a set in `<OptionList>`.
 * Arrow keys move between options, as a native radio group does.
 */
export function Option({
  selected,
  onSelect,
  leading,
  title,
  subtitle,
  trailing,
}: {
  selected: boolean;
  onSelect: () => void;
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: styled radio rows (WAI-ARIA radio pattern) carry rich content a native radio can't
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={selected ? 0 : -1}
      className="ul-option"
      onClick={onSelect}
    >
      {leading ? <span className="ul-option__leading">{leading}</span> : null}
      <span className="ul-option__body">
        <span className="ul-option__title">{title}</span>
        {subtitle ? <span className="ul-option__subtitle">{subtitle}</span> : null}
      </span>
      {trailing ? <span className="ul-option__trailing">{trailing}</span> : null}
      <span className="ul-option__mark" aria-hidden>
        {selected ? <Icon name="check" size={14} weight="bold" /> : null}
      </span>
    </button>
  );
}

export function OptionList({ label, children }: { label: string; children: ReactNode }) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    const items = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    e.preventDefault();
    const next =
      items[(i + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length];
    next?.focus();
    next?.click();
  };
  return (
    <div className="ul-options" role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {children}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible name; the visible label is usually the row title next to it. */
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className="ul-switch"
      onClick={() => onChange(!checked)}
    >
      <span className="ul-switch__thumb" aria-hidden />
    </button>
  );
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange?: (value: T) => void;
  label?: string;
}) {
  return (
    <div className="ul-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        // biome-ignore lint/a11y/useSemanticElements: segmented control is a styled radio group of buttons (WAI-ARIA radio pattern)
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className="ul-seg__opt"
          onClick={() => onChange?.(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function MoneyField({
  id,
  value,
  onChange,
  currency = '$',
  placeholder = '0.00',
  label,
}: {
  id?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  currency?: string;
  placeholder?: string;
  label?: string;
}) {
  const ui = useUiText();
  return (
    <label className="ul-field ul-field--money">
      <span className="ul-field__prefix">{currency}</span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        aria-label={label ?? ui.amount}
      />
    </label>
  );
}

export function SectionHeader({
  title,
  action,
  onAction,
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) {
  return (
    <div className="ul-sechead">
      <span className="ul-group__header" style={{ padding: 0 }}>
        {title}
      </span>
      {action ? (
        <button type="button" className="ul-link" onClick={onAction}>
          {action}
          <Icon name="chevron" size={16} />
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: IconName;
  title: string;
  text?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="ul-empty">
      <span className="ul-empty__icon">
        <Icon name={icon} size={24} />
      </span>
      <span className="ul-empty__title">{title}</span>
      {text ? <span className="ul-empty__text">{text}</span> : null}
      {action}
    </div>
  );
}

export function Skeleton({ height, radius }: { height: number; radius?: number }) {
  return <div className="ul-skeleton" style={{ height, borderRadius: radius }} aria-hidden />;
}

export function SearchField({
  id,
  value,
  onChange,
  placeholder,
  label,
  autoFocus,
}: {
  id?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="ul-field">
      <Icon name="search" size={18} />
      <input
        id={id}
        type="text"
        inputMode="url"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        aria-label={label ?? placeholder}
        // biome-ignore lint/a11y/noAutofocus: the search screen exists to type a name
        autoFocus={autoFocus}
      />
    </label>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  type = 'text',
  inputMode,
  autoComplete,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  type?: 'text' | 'email' | 'tel' | 'password';
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <div className={cx('ul-textfield', error && 'ul-textfield--error')}>
      <label htmlFor={id} className="ul-textfield__label">
        {label}
      </label>
      <input
        id={id}
        className="ul-textfield__input"
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error || undefined}
        aria-describedby={error || hint ? `${id}-note` : undefined}
      />
      {error || hint ? (
        <span id={`${id}-note`} className="ul-textfield__note">
          {error ?? hint}
        </span>
      ) : null}
    </div>
  );
}

/** Telegram photo, or the first letter of the name on the accent tint when there is none or it fails. */
export function Avatar({ src, name, size = 40 }: { src?: string | null; name: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="ul-avatar" style={{ width: size, height: size, fontSize: size * 0.42 }} aria-hidden>
      {src && !broken ? (
        <img src={src} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
      ) : (
        (name.trim()[0] ?? '?').toUpperCase()
      )}
    </span>
  );
}
